import { Link, useLocation, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, ArrowRight, Phone, FileText, CheckCircle, Wind } from "lucide-react";
import SEOHead from "../components/layout/SEOHead";
import { AddToCart } from "../components/cart/Cart";
import { useCommerce } from "../context/CommerceContext";
import { products, compatibilityNote, liftCompatibilityStatement, getProductBySlug } from "../data/products";

const renderParagraph = (paragraph, index) => {
  const parts = paragraph.split(/(\*\*.+?\*\*)/g);
  return (
    <p key={index} className="text-gray-300 leading-relaxed mb-4">
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**")
          ? <strong key={i} className="text-white">{part.slice(2, -2)}</strong>
          : part
      )}
    </p>
  );
};

const ApplicationPage = () => {
  const slug = useLocation().pathname.split("/").filter(Boolean).pop();
  const product = getProductBySlug(slug);
  const { commerceEnabled } = useCommerce();

  if (!product) {
    return <Navigate to="/#products" replace />;
  }

  const other = products.find((p) => p.id !== product.id);

  return (
    <>
      <SEOHead
        title={product.seo.title}
        description={product.seo.description}
        keywords={product.seo.keywords}
        canonicalPath={product.to}
        ogType="product"
        ogImage={product.image}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.fullName,
            "alternateName": product.descriptor,
            "model": product.model,
            "sku": product.sku,
            "description": product.seo.description,
            "image": product.image,
            "brand": { "@type": "Brand", "name": "Highwall Lift" },
            "offers": {
              "@type": "Offer",
              "price": product.price.replace("$", ""),
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": `https://www.highwalllift.com${product.to}`
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] pt-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-6">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <Link to="/#products" className="text-gray-400 hover:text-white transition-colors">Products</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <span className="text-white">{product.name}</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="py-8 sm:py-12" data-testid={`application-page-${product.id}`}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="space-y-4">
                <div className="aspect-[4/3] overflow-hidden border border-white/10 bg-[#1A1A1A]">
                  <img src={product.image} alt={product.imageAlt} className="w-full h-full object-cover" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {product.galleryImages.map((img, index) => (
                    <div key={index} className="aspect-square overflow-hidden border border-white/10 bg-[#1A1A1A]">
                      <img src={img} alt={`${product.fullName} (${product.model}) view ${index + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-xs font-bold mb-3">Highwall Lift</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white mb-2" data-testid="application-name">
                  {product.name}
                </h1>
                <p className="font-mono text-gray-400 uppercase tracking-[0.2em] text-sm mb-3" data-testid="application-model">{product.model}</p>
                <p className="font-heading text-xl sm:text-2xl font-semibold text-gray-200 mb-2" data-testid="application-descriptor">
                  {product.descriptor}
                </p>
                <p className="text-signal-red italic text-lg mb-6" data-testid="application-tagline">{product.tagline}</p>
                <p className="text-gray-400 leading-relaxed mb-8">{product.blurb}</p>

                <div className="mb-8">
                  <p className="text-gray-500 text-sm uppercase tracking-wide mb-1">Price</p>
                  <p className="font-heading text-4xl font-black text-white" data-testid="application-price">
                    {product.price}<span className="text-lg text-gray-400">{product.priceUnit}</span>
                  </p>
                </div>

                <div className="mb-8">
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-3">Primary Applications</p>
                  <ul className="space-y-2">
                    {product.applications.map((app) => (
                      <li key={app} className="flex items-center gap-2 text-gray-300">
                        <div className="w-1.5 h-1.5 bg-signal-red" />
                        {app}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-8">
                  <ul className="space-y-3">
                    {product.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-gray-300">
                        <CheckCircle className="w-5 h-5 text-signal-red flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {product.windNote && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm mt-4">
                      <Wind className="w-4 h-4" />
                      <span>{product.windNote}</span>
                    </div>
                  )}
                </div>

                <div className="mb-8 bg-[#1A1A1A] border border-white/10 p-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Lift Compatibility</p>
                  <p className="text-gray-300 text-sm" data-testid="application-lift-compatibility">{liftCompatibilityStatement}</p>
                </div>

                <div className="mb-6">
                  <AddToCart product={product} idPrefix={`application-${product.id}`} />
                  {commerceEnabled && <p className="text-gray-500 text-xs mt-2">Sold by the set. Shipping and tax calculated at checkout. U.S. online checkout; international orders by quote.</p>}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/#quote"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-bold uppercase tracking-wide border border-white/20 hover:bg-white/20 transition-all duration-300"
                    data-testid="application-quote-cta"
                  >
                    <FileText className="w-5 h-5" />
                    Request a Quote
                  </Link>
                  <a
                    href="tel:8067293701"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-bold uppercase tracking-wide border border-white/20 hover:bg-white/20 transition-all duration-300"
                    data-testid="application-call-cta"
                  >
                    <Phone className="w-5 h-5" />
                    (806) 729-3701
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About */}
        <section className="py-16 bg-[#141414]">
          <div className="max-w-4xl mx-auto px-6 sm:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-6">About the {product.fullName} ({product.model})</h2>
            <p className="text-gray-300 leading-relaxed text-lg mb-6" data-testid="application-positioning">{product.positioning}</p>
            <div className="prose prose-invert prose-lg max-w-none">
              {product.description.split("\n\n").map(renderParagraph)}
            </div>
          </div>
        </section>

        {/* Specifications */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6 sm:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-8">Specifications</h2>
            <div className="bg-[#1A1A1A] border border-white/10 overflow-hidden" data-testid="application-specs">
              {product.specs.map((spec, index) => (
                <div key={spec.label} className={`flex justify-between items-center gap-4 p-4 ${index !== product.specs.length - 1 ? "border-b border-white/10" : ""}`}>
                  <span className="text-gray-400">{spec.label}</span>
                  <span className="text-white font-medium text-right">{spec.value}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-6" data-testid="application-compatibility-note">{compatibilityNote}</p>
          </div>
        </section>

        {/* Other product */}
        <section className="py-16 bg-[#141414]">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-8">Other Highwall Lift Product</h2>
            <Link to={other.to} className="block bg-[#1A1A1A] border border-white/10 p-8 group max-w-2xl" data-testid={`other-application-${other.id}`}>
              <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-xs font-bold mb-2">Highwall Lift</p>
              <h3 className="font-heading text-2xl font-bold text-white mb-1 group-hover:text-signal-red transition-colors">{other.name}</h3>
              <p className="font-mono text-gray-500 uppercase tracking-[0.2em] text-xs mb-2">{other.model}</p>
              <p className="font-heading text-lg font-semibold text-gray-200 mb-1">{other.descriptor}</p>
              <p className="text-signal-red italic mb-4">{other.tagline}</p>
              <div className="flex items-center justify-between">
                <span className="font-heading text-2xl font-black text-white">
                  {other.price}<span className="text-sm text-gray-400">{other.priceUnit}</span>
                </span>
                <span className="flex items-center gap-1 text-signal-red font-bold uppercase text-sm">
                  View
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-signal-red">
          <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase text-white mb-6">Ready to Get Started?</h2>
            <p className="text-white/80 text-lg mb-8">We ship pronto. Request a quote today and get your equipment fast.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#quote" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black font-bold uppercase tracking-wide hover:bg-white/90 transition-all duration-300">
                <FileText className="w-5 h-5" />
                Request a Quote
              </Link>
              <a href="tel:8067293701" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white font-bold uppercase tracking-wide border-2 border-white hover:bg-white/10 transition-all duration-300">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ApplicationPage;
